(() => {
  if (customElements.get('every-terrain')) return;

  const squeezeQuery = window.matchMedia('(min-width: 990px)');
  const mobileQuery = window.matchMedia('(max-width: 919px)');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const easeOut = (value) => 1 - Math.pow(1 - value, 3);
  const smoothstep = (value) => value * value * (3 - 2 * value);
  const lerp = (from, to, progress) => from + (to - from) * progress;

  const getScrollContainer = () => {
    if (squeezeQuery.matches) {
      return document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const getScrollEventTarget = () => {
    if (squeezeQuery.matches) return document.querySelector('.page-wrapper') || document;
    return document;
  };

  class EveryTerrain extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.dataset.motionRuntime = 'connected';
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pinObserver = new ResizeObserver(() => this.requestMeasure());
      this.onScroll = this.requestFrame.bind(this);
      this.onResize = () => {
        // Mobile browser chrome changes innerHeight during a scroll. The layout
        // uses svh, so only a real width/orientation change needs remeasurement.
        if (mobileQuery.matches && this.layoutWidth === window.innerWidth) return;
        this.requestMeasure();
      };
      this.onLayoutChange = this.requestMeasure.bind(this);
      this.onScrollContainerChange = this.bindScrollTarget.bind(this);

      window.addEventListener('resize', this.onResize, { passive: true });
      this.motionQuery.addEventListener('change', this.onLayoutChange);
      mobileQuery.addEventListener('change', this.onLayoutChange);
      squeezeQuery.addEventListener('change', this.onScrollContainerChange);
      this.bindScrollTarget();
      requestAnimationFrame(() => {
        if (this.isConnected) this.refresh();
      });

      document.fonts?.ready.then(() => {
        if (this.isConnected) this.measure();
      });
    }

    disconnectedCallback() {
      this.connected = false;
      this.scrollTarget?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.motionQuery?.removeEventListener('change', this.onLayoutChange);
      mobileQuery.removeEventListener('change', this.onLayoutChange);
      squeezeQuery.removeEventListener('change', this.onScrollContainerChange);
      this.pinObserver?.disconnect();
      if (this.frameRequest) cancelAnimationFrame(this.frameRequest);
      if (this.measureRequest) cancelAnimationFrame(this.measureRequest);
      this.frameRequest = 0;
      this.measureRequest = 0;
      this.sectionWrapper?.removeAttribute('data-every-terrain-sticky');
    }

    bindScrollTarget() {
      this.scrollTarget?.removeEventListener('scroll', this.onScroll);
      this.scrollTarget = getScrollEventTarget();
      this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
      this.requestMeasure();
    }

    refresh() {
      this.pin = this.querySelector('[data-every-terrain-pin]');
      this.pinObserver.disconnect();
      if (this.pin) this.pinObserver.observe(this.pin);
      this.firstWord = this.querySelector('[data-every-terrain-first]');
      this.secondWord = this.querySelector('[data-every-terrain-second]');
      this.introMedia = this.querySelector('[data-every-terrain-intro-media]');
      this.rulerFirst = this.querySelector('[data-every-terrain-ruler-first]');
      this.rulerSecond = this.querySelector('[data-every-terrain-ruler-second]');
      this.rail = this.querySelector('.every-terrain__rail');
      this.sectionWrapper = this.closest('.every-terrain-shopify-section');
      this.cards = Array.from(this.querySelectorAll('[data-every-terrain-card]'));
      this.labels = this.cards.map((card) => card.querySelector('.every-terrain__label'));
      this.cards.forEach((card) => card.removeAttribute('data-featured'));
      this.featuredCard = this.cards[Math.floor(this.cards.length / 2)];
      this.featuredCard?.setAttribute('data-featured', 'true');
      this.featuredImage = this.featuredCard?.querySelector('.every-terrain__image');
      this.ensureIntroImage();
      this.requestMeasure();
    }

    ensureIntroImage() {
      if (!this.introMedia || !this.featuredImage) return;
      const configuredMainImage = this.introMedia.querySelector('.every-terrain__intro-image');
      if (configuredMainImage) {
        this.introImage = configuredMainImage;
        return;
      }
      const clone = this.featuredImage.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('loading');
      clone.alt = '';
      clone.setAttribute('aria-hidden', 'true');
      this.introMedia.replaceChildren(clone);
      this.introImage = clone;
    }

    unavailableReason() {
      if (this.dataset.enableAnimation !== 'true') return 'sticky-disabled';
      if (this.motionQuery.matches) return 'reduced-motion';
      if (!this.cards.length) return 'no-cards';
      if (!this.featuredCard || !this.featuredImage) return 'no-featured-image';
      return '';
    }

    reset() {
      this.removeAttribute('data-motion-ready');
      this.removeAttribute('data-done');
      this.removeAttribute('data-measuring');
      this.sectionWrapper?.removeAttribute('data-every-terrain-sticky');
      this.setInteractive(true);
      this.measurements = null;
      this.lastProgress = null;
      [this.firstWord, this.secondWord].forEach((word) => {
        if (word) word.style.cssText = '';
      });
      if (this.introMedia) this.introMedia.style.cssText = '';
      this.cards?.forEach((card) => {
        card.style.opacity = '';
        card.style.transform = '';
        card.style.visibility = '';
      });
      this.labels?.forEach((label) => {
        if (label) label.style.opacity = '';
      });
    }

    setInteractive(interactive) {
      this.toggleAttribute('data-cards-interactive', interactive);
      if (!this.rail) return;
      this.rail.inert = !interactive;
      if (interactive) this.rail.removeAttribute('aria-hidden');
      else this.rail.setAttribute('aria-hidden', 'true');
    }

    requestMeasure() {
      if (this.measureRequest) return;
      this.measureRequest = requestAnimationFrame(() => {
        this.measureRequest = 0;
        if (this.isConnected) this.measure();
      });
    }

    measure() {
      if (!this.pin || !this.firstWord || !this.secondWord || !this.introMedia || !this.rulerFirst || !this.rulerSecond) return;
      const unavailable = this.unavailableReason();
      if (unavailable) {
        this.reset();
        this.dataset.motionState = unavailable;
        return;
      }

      this.dataset.motionState = 'ready';
      this.layoutWidth = window.innerWidth;
      this.setAttribute('data-measuring', '');
      this.setAttribute('data-motion-ready', '');
      this.sectionWrapper?.toggleAttribute('data-every-terrain-sticky', this.dataset.stickySection === 'true');
      this.setInteractive(false);
      this.cards.forEach((card) => { card.style.transform = ''; });
      const pinRect = this.pin.getBoundingClientRect();
      this.viewport = pinRect.height;
      const relativeRect = (element) => {
        const rect = element.getBoundingClientRect();
        return {
          left: rect.left - pinRect.left,
          top: rect.top - pinRect.top,
          width: rect.width,
          height: rect.height,
        };
      };

      [this.firstWord, this.secondWord].forEach((word) => {
        word.style.position = '';
        word.style.left = '';
        word.style.top = '';
        word.style.transform = '';
        word.style.visibility = '';
        word.style.opacity = '';
      });
      this.introMedia.style.cssText = '';
      this.introMedia.style.width = '0px';
      const startWidth = mobileQuery.matches ? 96 : 150;
      const startHeight = mobileQuery.matches ? 122 : 190;
      this.introMedia.style.height = `${startHeight}px`;

      const firstStart = relativeRect(this.firstWord);
      const secondStart = relativeRect(this.secondWord);
      const centre = (firstStart.left + firstStart.width + secondStart.left) / 2;

      this.measurements = {
        firstStart,
        secondStart,
        imageStart: {
          left: centre,
          top: firstStart.top + firstStart.height / 2 - startHeight / 2,
          width: startWidth,
          height: startHeight,
        },
        firstEnd: relativeRect(this.rulerFirst),
        secondEnd: relativeRect(this.rulerSecond),
        imageEnd: relativeRect(this.featuredImage),
      };

      [[this.firstWord, firstStart], [this.secondWord, secondStart]].forEach(([word, rect]) => {
        word.style.position = 'absolute';
        word.style.left = `${rect.left}px`;
        word.style.top = `${rect.top}px`;
      });
      this.introMedia.style.position = 'absolute';
      this.introMedia.style.left = `${this.measurements.imageStart.left}px`;
      this.introMedia.style.top = `${this.measurements.imageStart.top}px`;
      this.lastProgress = null;
      this.render();
      this.removeAttribute('data-measuring');
    }

    requestFrame() {
      if (this.frameRequest || !this.measurements) return;
      this.frameRequest = requestAnimationFrame(() => {
        this.frameRequest = 0;
        this.render();
      });
    }

    placeWord(element, start, end, initialOffset, progress) {
      const scale = lerp(1, end.width / Math.max(start.width, 1), progress);
      const x = lerp(initialOffset, end.left - start.left, progress);
      const y = lerp(0, end.top - start.top, progress);
      element.style.transform = `translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,0) scale(${scale.toFixed(4)})`;
    }

    orderedCards() {
      const featuredIndex = Math.max(0, this.cards.indexOf(this.featuredCard));
      const output = [];
      for (let distance = 1; output.length < this.cards.length - 1; distance += 1) {
        const left = this.cards[featuredIndex - distance];
        const right = this.cards[featuredIndex + distance];
        if (left) output.push({ card: left, direction: -1, distance });
        if (right) output.push({ card: right, direction: 1, distance });
        if (!left && !right) break;
      }
      return output;
    }

    render() {
      if (!this.measurements) return;
      const bounds = this.getBoundingClientRect();
      const container = getScrollContainer();
      const viewportTop = container === document.scrollingElement || container === document.documentElement
        ? 0 : container.getBoundingClientRect().top;
      const hold = this.viewport;
      const run = Math.max(bounds.height - this.viewport - hold, 1);
      const progress = clamp((viewportTop - bounds.top) / run);
      if (progress === this.lastProgress) return;
      this.lastProgress = progress;
      const widenProgress = easeOut(clamp(progress / .30));
      const wedgeWidth = this.measurements.imageStart.width * widenProgress;
      const wordPush = (wedgeWidth + 32 * widenProgress) / 2;
      const flightProgress = smoothstep(clamp((progress - .38) / .48));

      this.placeWord(this.firstWord, this.measurements.firstStart, this.measurements.firstEnd, -wordPush, flightProgress);
      this.placeWord(this.secondWord, this.measurements.secondStart, this.measurements.secondEnd, wordPush, flightProgress);

      const wordOpacity = 1 - .9 * smoothstep(clamp((progress - .52) / .32));
      this.firstWord.style.opacity = wordOpacity.toFixed(3);
      this.secondWord.style.opacity = wordOpacity.toFixed(3);

      const { imageStart, imageEnd } = this.measurements;
      this.introMedia.style.left = `${lerp(imageStart.left - wedgeWidth / 2, imageEnd.left, flightProgress).toFixed(2)}px`;
      this.introMedia.style.top = `${lerp(imageStart.top, imageEnd.top, flightProgress).toFixed(2)}px`;
      this.introMedia.style.width = `${lerp(wedgeWidth, imageEnd.width, flightProgress).toFixed(2)}px`;
      this.introMedia.style.height = `${lerp(imageStart.height, imageEnd.height, flightProgress).toFixed(2)}px`;

      const done = flightProgress >= .985;
      this.toggleAttribute('data-done', done);
      this.setInteractive(progress >= .99);
      const visibility = done ? 'hidden' : 'visible';
      this.introMedia.style.visibility = visibility;
      this.firstWord.style.visibility = visibility;
      this.secondWord.style.visibility = visibility;

      const featuredIndex = this.cards.indexOf(this.featuredCard);
      [featuredIndex, featuredIndex - 1, featuredIndex + 1, featuredIndex - 2, featuredIndex + 2]
        .filter((index) => index >= 0 && index < this.labels.length)
        .forEach((labelIndex, orderIndex) => {
          const label = this.labels[labelIndex];
          if (label) label.style.opacity = smoothstep(clamp((progress - (.62 + orderIndex * .045)) / .14)).toFixed(3);
        });

      this.featuredCard.style.opacity = '1';
      this.featuredCard.style.visibility = progress > .62 ? 'visible' : 'hidden';
      this.orderedCards().forEach(({ card, direction, distance }, orderIndex) => {
        const reveal = smoothstep(clamp((progress - (.66 + orderIndex * .05)) / .16));
        card.style.opacity = reveal.toFixed(3);
        card.style.visibility = reveal > 0 ? 'visible' : 'hidden';
        card.style.transform = done ? '' : `translate3d(${lerp(direction * (distance === 1 ? 34 : 56), 0, reveal).toFixed(2)}px,0,0)`;
      });
    }
  }

  customElements.define('every-terrain', EveryTerrain);
})();
